import { colorToString } from './color'
import { applyRotation } from './drawHandles'
import { expandStops } from '@/components/workspace/workspace-editor/workspace-editor-menu/common/gradient-utils'
import { type ImageCache } from './types'
import {
  type LayoutNode,
  type TextOptions,
  type TextNodeData,
  type RichTextRun,
  type LinearGradient,
  type BorderRadiusCorners,
  runAtCharOffset,
} from '@backend/lib/db/typeRoots/layoutNode'
import { type Color } from '@backend/lib/db/typeRoots/color'
import { rewrapFromContent } from '@/lib/rewrapTextNodesFromRuns'

/** Pick per-corner radii when present, otherwise the uniform scalar. */
const radiusFor = (
  corners: BorderRadiusCorners | undefined,
  uniform: number,
): number | BorderRadiusCorners => corners ?? uniform

export default (
  backgroundColor: Color,
  nodes: LayoutNode[],
  images: ImageCache,
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  overlayOpacity = 0,
) => {
  // Virtual canvas background
  ctx.fillStyle = colorToString(backgroundColor)
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  // Draw nodes
  nodes
    .filter((node) => node.visible)
    .forEach((node) => {
      ctx.save()

      applyRotation(ctx, node)

      switch (node.data.type) {
        case 'image': {
          const cacheKey = node.data.source.url
          drawImage(node, images[cacheKey], ctx)
          break
        }

        case 'text':
          drawText(node, ctx)
          break

        case 'shape':
          drawShape(node, ctx)
          break
      }

      ctx.restore()
    })

  // Overlay outside the virtual canvas boundary — drawn after nodes so it covers overflowing assets
  if (overlayOpacity > 0) {
    ctx.save()
    ctx.beginPath()
    ctx.rect(-10000, -10000, 20000, 20000)
    ctx.rect(0, 0, canvasWidth, canvasHeight)
    ctx.clip('evenodd')
    ctx.fillStyle = `rgba(0, 0, 0, ${overlayOpacity})`
    ctx.fillRect(-10000, -10000, 20000, 20000)
    ctx.restore()
  }
}

function createCanvasLinearGradient(
  ctx: CanvasRenderingContext2D,
  gradient: LinearGradient,
  x: number,
  y: number,
  width: number,
  height: number,
): CanvasGradient {
  const angleRad = ((gradient.angle - 90) * Math.PI) / 180
  const cx = x + width / 2
  const cy = y + height / 2
  // CSS-spec gradient line: length scales to exactly cover the box at any angle
  const halfLen = (Math.abs(width * Math.cos(angleRad)) + Math.abs(height * Math.sin(angleRad))) / 2
  const dx = Math.cos(angleRad) * halfLen
  const dy = Math.sin(angleRad) * halfLen
  const grad = ctx.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy)
  for (const stop of expandStops(gradient.stops)) {
    grad.addColorStop(stop.position, colorToString(stop.color))
  }
  return grad
}

const drawImage = (
  node: LayoutNode,
  image: HTMLImageElement | undefined,
  ctx: CanvasRenderingContext2D,
) => {
  if (!image || !image.complete) {
    ctx.fillStyle = 'rgba(90, 154, 250, 1)'
    ctx.fillRect(node.x, node.y, node.width, node.height)

    ctx.font = `24px serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = 'white'
    ctx.fillText('Loading...', node.x + node.width / 2, node.y + node.height / 2)
    return
  }

  const { options } = node.data as Extract<LayoutNode['data'], { type: 'image' }>

  if (options.mode === 'distort') {
    if (options.borderWidth > 0) {
      ctx.beginPath()
      ctx.roundRect(
        node.x,
        node.y,
        node.width,
        node.height,
        radiusFor(options.borderRadiusCorners, options.borderRadius),
      )
      ctx.closePath()

      // ctx.globalAlpha = options.borderColor.a !== undefined ? options.borderColor.a / 255 : 1
      ctx.lineWidth = options.borderWidth
      // ctx.strokeStyle = getColor(options.borderColor, colorPalette)
      ctx.strokeStyle = colorToString(options.borderColor)
      ctx.stroke()
    }

    ctx.save()
    ctx.beginPath()
    ctx.roundRect(
      node.x,
      node.y,
      node.width,
      node.height,
      radiusFor(options.borderRadiusCorners, options.borderRadius),
    )
    ctx.closePath()
    ctx.clip()

    ctx.globalAlpha = node.data.isLoading ? 0.5 : options.opacity
    ctx.drawImage(image, node.x, node.y, node.width, node.height)
  } else {
    const imageAspectRatio = image.width / image.height
    const nodeAspectRatio = node.width / node.height

    let width = node.width
    let height = node.height
    let x = node.x
    let y = node.y

    let sx = 0
    let sy = 0
    let sw = image.width
    let sh = image.height

    if (options.mode === 'contain') {
      if (imageAspectRatio < nodeAspectRatio) {
        // image is wider than node
        width = node.height * imageAspectRatio
        x = node.x + (node.width - width) / 2
      } else {
        // image is taller than node
        height = node.width / imageAspectRatio
        y = node.y + (node.height - height) / 2
      }
    } else if (options.mode === 'cover') {
      if (imageAspectRatio < nodeAspectRatio) {
        // image is taller than node
        // height = node.width / imageAspectRatio
        // y = node.y + (node.height - height) / 2
        sy = (image.height - (image.width * node.height) / node.width) / 2
        sh = (image.width * node.height) / node.width
      } else {
        // image is wider than node
        // width = node.height * imageAspectRatio
        // x = node.x + (node.width - width) / 2
        sx = (image.width - (image.height * node.width) / node.height) / 2
        sw = (image.height * node.width) / node.height
      }
    }

    if (options.borderWidth > 0) {
      ctx.beginPath()
      ctx.roundRect(
        x,
        y,
        width,
        height,
        radiusFor(options.borderRadiusCorners, options.borderRadius),
      )
      ctx.closePath()

      // ctx.globalAlpha = options.borderColor.a !== undefined ? options.borderColor.a / 255 : 1
      ctx.lineWidth = options.borderWidth
      ctx.strokeStyle = colorToString(options.borderColor)
      ctx.stroke()
    }

    ctx.save()
    ctx.beginPath()
    ctx.roundRect(x, y, width, height, radiusFor(options.borderRadiusCorners, options.borderRadius))
    ctx.closePath()
    ctx.clip()

    ctx.globalAlpha = node.data.isLoading ? 0.5 : options.opacity
    ctx.drawImage(image, sx, sy, sw, sh, x, y, width, height)
  }

  ctx.restore()
}

// NOTE: comment out autofit on resize
// const drawText = (node: LayoutNode, content: string, ctx: CanvasRenderingContext2D) => {
//   const { options, editMode } = node.data as Extract<LayoutNode['data'], { type: 'text' }>

//   const {
//     fontFamily,
//     fontColor,
//     lineHeight,
//     strokeWidth,
//     strokeColor,
//     horizontalAlign,
//     verticalAlign,
//     backdrop,
//   } = options

//   // Binary search for best font size
//   let minSize = 1
//   let maxSize = 200 // or some reasonable maximum
//   let fontSize = options.fontSize // initial guess
//   let bestFitSize = fontSize
//   let bestFitLines: string[] = []

//   while (maxSize - minSize > 0.5) {
//     ctx.font = `${fontSize}px ${fontFamily}`
//     const lines = breakLines(content, node.width, ctx, fontSize, fontFamily)
//     const textHeight = calculateTextHeight(lines, fontSize, lineHeight)

//     if (textHeight > node.height) {
//       // Too big, try smaller
//       maxSize = fontSize
//       fontSize = (minSize + fontSize) / 2
//     } else {
//       // Fits, try bigger
//       bestFitSize = fontSize
//       bestFitLines = lines
//       minSize = fontSize
//       fontSize = (maxSize + fontSize) / 2
//     }
//   }

//   fontSize = bestFitSize
//   const textHeight = calculateTextHeight(bestFitLines, fontSize, lineHeight)

//   if (backdrop) {
//     drawTextBackdrop(node, bestFitLines, options, ctx)
//   }

//   if (editMode) return

//   const x =
//     horizontalAlign === 'center'
//       ? node.x + node.width / 2
//       : horizontalAlign === 'right'
//         ? node.x + node.width
//         : node.x

//   const y =
//     verticalAlign === 'center'
//       ? node.y + node.height / 2 - textHeight / 2
//       : verticalAlign === 'bottom'
//         ? node.y + node.height - textHeight
//         : node.y

//   ctx.font = `${fontSize}px ${fontFamily}`
//   ctx.textAlign = horizontalAlign
//   ctx.textBaseline = 'top'

//   ctx.strokeStyle = getColor(strokeColor, colorPalette)
//   ctx.lineWidth = strokeWidth

//   bestFitLines.forEach((line, i) => {
//     ctx.fillStyle = getColor(fontColor, colorPalette)
//     ctx.fillText(line, x, y + i * lineHeight * fontSize)

//     if (strokeWidth > 0) {
//       ctx.strokeText(line, x, y + i * lineHeight * fontSize)
//     }
//   })
// }

const drawText = (node: LayoutNode, ctx: CanvasRenderingContext2D) => {
  const data = node.data as Extract<LayoutNode['data'], { type: 'text' }>
  const { options, editMode, content: contentRuns } = data
  const { fontFamily, fontWeight, fontStyle, fontSize, fontColor, strokeWidth, strokeColor } =
    options

  if ((node.data as TextNodeData).isLoading) {
    ctx.globalAlpha = 0.5
  } else if (options.opacity != null && options.opacity < 1) {
    // CSS opacity stacks across ancestors and dims both the backdrop and
    // the glyphs uniformly. Apply once via globalAlpha so the backdrop
    // (drawn next) and the text below inherit it identically.
    ctx.globalAlpha = options.opacity
  }

  // Single render path. When the captured layout is missing (a fresh node
  // added via "Add Text", or a layout-affecting toolbar change that cleared
  // it), derive one inline from the content runs. There used to be a
  // legacy fallback painter that re-derived everything from options-level
  // style — it dropped per-run colors and used different baseline math, so
  // any options change visibly drifted the text. Now there's just one path.
  const textNode = node as LayoutNode & { data: Extract<LayoutNode['data'], { type: 'text' }> }
  const captured =
    options.preWrappedLines && options.preWrappedLines.length > 0
      ? options.preWrappedLines
      : (rewrapFromContent(textNode, ctx) ?? [])

  drawTextBackdrop(
    node,
    captured.map((l) => l.text),
    options,
    ctx,
  )
  if (editMode || captured.length === 0) return

  ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}"`
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  ctx.strokeStyle = colorToString(strokeColor)
  ctx.lineWidth = strokeWidth
  ctx.fillStyle = colorToString(fontColor)

  // line.x/y are stored relative to the node's top-left so the captured
  // layout translates with the node when it moves. The captured y for
  // each line corresponds to where canvas's textBaseline='top' would
  // anchor, so the typographic baseline sits at y + fontBoundingBoxAscent.
  // Probed via 'Hg' (a string with both an ascender and a descender) so
  // the ascent reflects the font's full vertical extent.
  const fallbackFont = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}"`
  const fallbackAscent = (() => {
    ctx.font = fallbackFont
    return ctx.measureText('Hg').fontBoundingBoxAscent
  })()

  // Inline styling resolves from `content` runs via character offset into
  // the joined content. Legacy nodes still carry styling on `pwlRun.*`
  // directly; when present we use those instead of the content lookup.
  const optionsStyle: RichTextRun = {
    text: '',
    fontFamily,
    fontSize,
    fontWeight,
    fontStyle,
    fontColor,
  }
  const styleForRun = (
    pwlRun: NonNullable<NonNullable<typeof captured>[number]['runs']>[number],
    offset: number,
  ): RichTextRun => {
    if (pwlRun.fontFamily !== undefined) {
      return {
        text: pwlRun.text,
        fontFamily: pwlRun.fontFamily,
        fontSize: pwlRun.fontSize ?? fontSize,
        fontWeight: pwlRun.fontWeight ?? fontWeight,
        fontStyle: pwlRun.fontStyle ?? fontStyle,
        fontColor: pwlRun.color ?? fontColor,
      }
    }
    return runAtCharOffset(contentRuns, offset) ?? optionsStyle
  }

  let charOffset = 0
  for (const line of captured) {
    const lineX = node.x + line.x

    // When inline-styled runs are present, we need to:
    //   1. Find the baseline by taking the MAX ascent across runs (so
    //      the tallest run's top aligns with line.y, and shorter runs
    //      sit on the same baseline like the browser draws them).
    //   2. Paint each run with its own font + color at line.x + run.x.
    // When no runs are captured (legacy fixtures or single-style lines),
    // fall back to drawing the whole line with the content style at the
    // line's starting offset (defaults to TextOptions if content is empty).
    if (line.runs && line.runs.length > 0) {
      let maxAscent = 0
      const runFonts: string[] = []
      const runStyles: RichTextRun[] = []
      let lineCharOffset = charOffset
      for (const run of line.runs) {
        const style = styleForRun(run, lineCharOffset)
        const runFont = `${style.fontStyle} ${style.fontWeight} ${style.fontSize}px "${style.fontFamily}"`
        runFonts.push(runFont)
        runStyles.push(style)
        ctx.font = runFont
        const a = ctx.measureText('Hg').fontBoundingBoxAscent
        if (a > maxAscent) maxAscent = a
        lineCharOffset += run.text.length
      }
      const baselineY = node.y + line.y + maxAscent
      for (let i = 0; i < line.runs.length; i++) {
        const run = line.runs[i]!
        const style = runStyles[i]!
        ctx.font = runFonts[i]!
        ctx.fillStyle = colorToString(style.fontColor)
        const runX = lineX + run.x
        ctx.fillText(run.text, runX, baselineY)
        if (strokeWidth > 0) ctx.strokeText(run.text, runX, baselineY)
      }
      ctx.fillStyle = colorToString(fontColor)
      charOffset = lineCharOffset + 1 // +1 for the '\n' between lines in content
      continue
    }

    const lineStyle = runAtCharOffset(contentRuns, charOffset) ?? optionsStyle
    const lineFont = `${lineStyle.fontStyle} ${lineStyle.fontWeight} ${lineStyle.fontSize}px "${lineStyle.fontFamily}"`
    ctx.font = lineFont
    ctx.fillStyle = colorToString(lineStyle.fontColor)
    const ascent =
      lineStyle === optionsStyle ? fallbackAscent : ctx.measureText('Hg').fontBoundingBoxAscent
    const baselineY = node.y + line.y + ascent
    ctx.fillText(line.text, lineX, baselineY)
    if (strokeWidth > 0) ctx.strokeText(line.text, lineX, baselineY)
    ctx.fillStyle = colorToString(fontColor)
    charOffset += line.text.length + 1
  }
}

const drawShape = (node: LayoutNode, ctx: CanvasRenderingContext2D) => {
  const { options } = node.data as Extract<LayoutNode['data'], { type: 'shape' }>
  const { shape, fillColor, fillOpacity, strokeColor, strokeOpacity, lineWidth, borderRadius } =
    options
  const radius = radiusFor(options.borderRadiusCorners, borderRadius)
  // fill
  ctx.fillStyle = options.fillGradient
    ? createCanvasLinearGradient(ctx, options.fillGradient, node.x, node.y, node.width, node.height)
    : colorToString(fillColor)
  // stroke
  ctx.lineWidth = lineWidth
  ctx.strokeStyle = colorToString(strokeColor)
  ctx.miterLimit = 2

  // shadow
  // ctx.shadowColor = resolvedParams.shadowColor
  // ctx.shadowOffsetX = resolvedParams.shadowOffsetX
  // ctx.shadowOffsetY = resolvedParams.shadowOffsetY
  // ctx.shadowBlur = resolvedParams.shadowBlur

  switch (shape) {
    case 'rectangle':
      ctx.beginPath()
      ctx.roundRect(node.x, node.y, node.width, node.height, radius)
      ctx.closePath()

      ctx.globalAlpha = fillOpacity
      ctx.fill()

      if (lineWidth > 0) {
        ctx.globalAlpha = strokeOpacity
        ctx.stroke()
      }
      break

    case 'circle':
      ctx.beginPath()
      ctx.arc(
        node.x + node.width / 2,
        node.y + node.height / 2,
        Math.min(node.width, node.height) / 2,
        0,
        2 * Math.PI,
      )

      ctx.globalAlpha = fillOpacity
      ctx.fill()

      if (lineWidth > 0) {
        ctx.globalAlpha = strokeOpacity
        ctx.stroke()
      }
      break

    case 'triangle':
      ctx.beginPath()
      ctx.moveTo(node.x + node.width / 2, node.y)
      ctx.lineTo(node.x + node.width, node.y + node.height)
      ctx.lineTo(node.x, node.y + node.height)
      ctx.closePath()

      ctx.globalAlpha = fillOpacity
      ctx.fill()

      if (lineWidth > 0) {
        ctx.globalAlpha = strokeOpacity
        ctx.stroke()
      }
      break
  }
}

const drawTextBackdrop = (
  node: LayoutNode,
  lines: string[],
  options: TextOptions,
  ctx: CanvasRenderingContext2D,
) => {
  let width = 0
  lines.forEach((line) => {
    const metrics = ctx.measureText(line)
    width = Math.max(width, metrics.width)
  })

  ctx.beginPath()
  ctx.roundRect(
    node.x - options.backdropPadding,
    node.y - options.backdropPadding,
    node.width + options.backdropPadding * 2,
    node.height + options.backdropPadding * 2,
    radiusFor(options.backdropBorderRadiusCorners, options.backdropBorderRadius),
  )
  ctx.closePath()

  if (options.backdropBorderWidth > 0) {
    ctx.lineWidth = options.backdropBorderWidth
    ctx.strokeStyle = colorToString(options.backdropBorderColor)
    ctx.stroke()
  }

  const backdropX = node.x - options.backdropPadding
  const backdropY = node.y - options.backdropPadding
  const backdropW = node.width + options.backdropPadding * 2
  const backdropH = node.height + options.backdropPadding * 2
  ctx.fillStyle = options.backdropGradient
    ? createCanvasLinearGradient(
        ctx,
        options.backdropGradient,
        backdropX,
        backdropY,
        backdropW,
        backdropH,
      )
    : colorToString(options.backdropColor)
  ctx.fill()
}

export const breakLines = (
  content: string,
  maxWidth: number,
  ctx: CanvasRenderingContext2D,
  fontSize: number,
  fontFamily: string,
  fontWeight: number,
  fontStyle: 'normal' | 'italic',
) => {
  ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}"`
  const lines = content.split('\n')

  for (let l = 0; l < lines.length; l++) {
    const words = lines[l]!.split(' ')
    for (let w = 0; w < words.length; w++) {
      if (ctx.measureText(words.slice(0, w + 1).join(' ')).width > maxWidth && w > 0) {
        lines[l] = words.slice(0, w).join(' ')
        lines.splice(l + 1, 0, words.slice(w).join(' '))
        break
      }
    }
  }

  for (let l = 0; l < lines.length; l++) {
    const words = lines[l]!.split(' ')
    for (let w = 0; w < words.length; w++) {
      const chars = words[0]!.split('')
      for (let c = 0; c < chars.length; c++) {
        if (ctx.measureText(chars.slice(0, c + 1).join('')).width > maxWidth && c > 0) {
          lines[l] = chars.slice(0, c).join('')
          lines.splice(l + 1, 0, chars.slice(c).join(''))
          break
        }
      }
    }
  }

  return lines
}

export const calculateTextHeight = (lines: string[], fontSize: number, lineHeight: number) => {
  return fontSize + Math.max(0, lineHeight * fontSize * (lines.length - 1))
}
